import { Button, Flex, LoadingOverlay } from "@mantine/core";
import { memo, useState } from "react";
import { AvatarIconAndNameLinkMemo } from "src/component/element";
import { ConfirmModalMemo } from "src/component/element/ConfirmModal/ConfirmModal";
import { WithdrawalConfirmModalMemo } from "src/component/pageComponent/Setting/Withdrawal/WithdrawalConfirmModal";
import {
  useCancelWithdrawalMutation,
  useReactiveAccountByAdminMutation,
} from "src/service/AdminApi/AdminUsers";
import type { GetUserData } from "src/service/UsersApi/type";

interface UserActionButtonsProps {
  user: GetUserData;
  onUpdate?: () => void;
}

const UserActionButtons = ({ user, onUpdate }: UserActionButtonsProps) => {
  const [isReactive, setIsReactive] = useState(false);
  const [isCancelWithdrawal, setIsCancelWithdrawal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [reactive, { isLoading }] = useReactiveAccountByAdminMutation();
  const [cancelWithdrawal] = useCancelWithdrawalMutation();

  const reactiveAccount = async () => {
    await reactive({ userId: user.id })
      .unwrap()
      .then(() => {
        setIsReactive(false);
      })
      .catch(() => {
        setIsReactive(false);
      })
      .finally(() => onUpdate?.());
  };

  const onCancelDeleteAccount = async () => {
    await cancelWithdrawal({ userId: user.id })
      .unwrap()
      .then(() => {
        setIsCancelWithdrawal(false);
      })
      .catch(() => {
        setIsCancelWithdrawal(false);
      })
      .finally(() => onUpdate?.());
  };

  const action = () => {
    if (user.isDeleted) {
      setIsReactive(true);
    } else if (user.scheduledDeletionAt) {
      setIsCancelWithdrawal(true);
    } else {
      setIsDelete(true);
    }
  };

  const onCloseWithdrawalConfirmModal = async () => {
    setIsDelete(false);
    onUpdate?.();
  };

  return (
    <>
      <Button
        onClick={action}
        size="xs"
        variant="subtle"
        sx={{ padding: 0 }}
        color={
          user.isDeleted
            ? "#00a5b9"
            : user.scheduledDeletionAt
            ? "orange"
            : "red"
        }
      >
        {user.isDeleted
          ? "アカウント復活"
          : user.scheduledDeletionAt
          ? "削除を取り消す"
          : "削除"}
      </Button>
      <LoadingOverlay visible={isLoading} />

      {isDelete && (
        <WithdrawalConfirmModalMemo
          userId={user.id}
          isAdmin
          opened
          onClose={onCloseWithdrawalConfirmModal}
        />
      )}

      {isReactive && (
        <ConfirmModalMemo
          opened
          title={
            <Flex pb={5}>
              <AvatarIconAndNameLinkMemo
                userId={user.id || ""}
                profileImage={user.profileImage}
                nickname={user.nickname || ""}
              />
            </Flex>
          }
          message={"このアカウントを復活してもよろしいですか?"}
          onClose={() => setIsReactive(false)}
          onConfirm={reactiveAccount}
        />
      )}

      {isCancelWithdrawal && (
        <ConfirmModalMemo
          opened
          title={
            <Flex pb={5}>
              <AvatarIconAndNameLinkMemo
                userId={user.id || ""}
                profileImage={user.profileImage}
                nickname={user.nickname || ""}
              />
            </Flex>
          }
          message={"アカウント削除をキャンセルしてもよろしいですか?"}
          onClose={() => setIsCancelWithdrawal(false)}
          onConfirm={onCancelDeleteAccount}
        />
      )}
    </>
  );
};

export const UserActionButtonsMemo = memo(UserActionButtons);
